const baseAPI_URL = process.env.REACT_APP_BACKEND_API_BASE_ENDPOINT;

function buildQuery(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    search.set(key, String(value));
  });
  const query = search.toString();
  return query ? `?${query}` : "";
}

function extractErrorMessage(data, fallback) {
  return data?.error || data?.message || fallback;
}

async function adminRequest(path, token, options = {}) {
  const response = await fetch(`${baseAPI_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(extractErrorMessage(data, "Erro na requisição administrativa."));
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
}

export async function getAdminSession(token) {
  return adminRequest("/admin/session", token);
}

export async function getAdminStats(token, { startDate, endDate }) {
  return adminRequest(
    `/admin/stats${buildQuery({ start_date: startDate, end_date: endDate })}`,
    token
  );
}

export async function getAdminStatsSeries(token, { startDate, endDate }) {
  return adminRequest(
    `/admin/stats/series${buildQuery({ start_date: startDate, end_date: endDate })}`,
    token
  );
}

export async function getAdminSales(
  token,
  { startDate, endDate, offset = 0, limit = 20, q, subscriptionStatus, paymentStatus } = {}
) {
  return adminRequest(
    `/admin/sales${buildQuery({
      start_date: startDate,
      end_date: endDate,
      offset,
      limit,
      q,
      subscription_status: subscriptionStatus,
      payment_status: paymentStatus,
    })}`,
    token
  );
}

export async function getCheckoutFunnel(token, { startDate, endDate }) {
  return adminRequest(
    `/admin/checkout/funnel${buildQuery({ start_date: startDate, end_date: endDate })}`,
    token
  );
}

export async function getCheckoutErrors(token, { startDate, endDate }) {
  return adminRequest(
    `/admin/checkout/errors${buildQuery({ start_date: startDate, end_date: endDate })}`,
    token
  );
}

export async function getAdminUsers(token, { offset = 0, limit = 20, q, accountType } = {}) {
  return adminRequest(
    `/admin/users${buildQuery({
      offset,
      limit,
      q,
      account_type: accountType,
    })}`,
    token
  );
}

export async function getAdminUser(token, userId) {
  return adminRequest(`/admin/users/${encodeURIComponent(userId)}`, token);
}

export async function getAdminUserBilling(token, userId) {
  return adminRequest(`/admin/users/${encodeURIComponent(userId)}/billing`, token);
}

export async function updateAdminUserEmail(token, userId, email) {
  return adminRequest(`/admin/users/${encodeURIComponent(userId)}/email`, token, {
    method: "PUT",
    body: JSON.stringify({ email }),
  });
}

export async function updateAdminUserPassword(token, userId, password) {
  return adminRequest(`/admin/users/${encodeURIComponent(userId)}/password`, token, {
    method: "PUT",
    body: JSON.stringify({ password }),
  });
}

export async function resetAdminUserAccess(token, userId) {
  return adminRequest(`/admin/users/${encodeURIComponent(userId)}/reset-access`, token, {
    method: "POST",
  });
}

export async function getAdminCourses(token) {
  return adminRequest("/admin/courses", token);
}

export async function getAdminCourse(token, courseCode) {
  return adminRequest(`/admin/courses/${encodeURIComponent(courseCode)}`, token);
}

export async function updateAdminCourse(token, courseCode, payload) {
  return adminRequest(`/admin/courses/${encodeURIComponent(courseCode)}`, token, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function getAdminLesson(token, courseCode, sectionCode, lessonCode) {
  return adminRequest(
    `/admin/courses/${encodeURIComponent(courseCode)}/sections/${encodeURIComponent(sectionCode)}/lessons/${encodeURIComponent(lessonCode)}`,
    token
  );
}

export async function getAttachmentUploadUrl(
  token,
  { fileName, fileType, courseId, sectionId, lessonId }
) {
  return adminRequest("/attachments/upload-url", token, {
    method: "POST",
    body: JSON.stringify({ fileName, fileType, courseId, sectionId, lessonId }),
  });
}

export async function uploadAttachmentToS3(uploadUrl, file) {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!response.ok) {
    throw new Error("Falha ao enviar o arquivo.");
  }
}

export async function createAdminAttachment(token, { lessonId, title, link }) {
  return adminRequest("/attachments", token, {
    method: "POST",
    body: JSON.stringify({ id: lessonId, title, link }),
  });
}

export async function updateAdminLesson(token, courseCode, sectionCode, lessonCode, payload) {
  return adminRequest(
    `/admin/courses/${encodeURIComponent(courseCode)}/sections/${encodeURIComponent(sectionCode)}/lessons/${encodeURIComponent(lessonCode)}`,
    token,
    {
      method: "PUT",
      body: JSON.stringify(payload),
    }
  );
}

export async function createAdminLesson(token, courseCode, sectionCode, payload) {
  return adminRequest(
    `/admin/courses/${encodeURIComponent(courseCode)}/sections/${encodeURIComponent(sectionCode)}/lessons`,
    token,
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  );
}

export async function reorderAdminLessons(token, courseCode, sectionCode, lessonCodes) {
  return adminRequest(
    `/admin/courses/${encodeURIComponent(courseCode)}/sections/${encodeURIComponent(sectionCode)}/reorder-lessons`,
    token,
    {
      method: "PUT",
      body: JSON.stringify({ lesson_codes: lessonCodes }),
    }
  );
}
